import { createFileRoute, redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { AuctionDetailInner } from "./auctions.$id";
import { buildAuctionSlug, extractIdFromSlug } from "@/lib/auction-slug";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { transformSupabaseImage } from "@/lib/image-transform";
import { buildAuctionFaq, auctionFaqLd } from "@/lib/auction-faq";
import { buildAuctionAlt } from "@/lib/image-alt";
import { buildProductLd } from "@/lib/product-schema";

type AuctionSeo = {
  id: string;
  title: string;
  description: string | null;
  images: string[] | null;
  current_bid_pence: number | null;
  starting_price_pence: number | null;
  end_time: string | null;
  grading_company: string | null;
  grade: string | null;
  status: string | null;
  product_type: string | null;
  accessory_category: string | null;
  brand: string | null;
  product_line_set: string | null;
  compatible_game: string | null;
  card_set: string | null;
  card_number: string | null;
  custom_grading_company: string | null;
  custom_grade: string | null;
  grading_status: string | null;
  grading_cert_number: string | null;
  certification_number: string | null;
  auto_extend_enabled: boolean | null;
  auto_extend_seconds: number | null;
  auto_extend_trigger_seconds: number | null;
  buy_now_enabled: boolean | null;
  buy_now_price_pence: number | null;
  created_at: string | null;
  start_time: string | null;
  condition: string | null;
} | null;


export const Route = createFileRoute("/auction/$slug")({
  loader: async ({ params, context }): Promise<{ auction: AuctionSeo }> => {
    const id = extractIdFromSlug(params.slug);
    if (!id) return { auction: null };
    try {
      // Fetch the full row once so the AuctionDetailInner component's
      // useQuery(["auction", id]) can hydrate from the router-primed cache
      // instead of firing a second identical Supabase request.
      const { data: full } = await supabase
        .from("auctions")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      const row = (full as AuctionSeo & Record<string, unknown> | null) ?? null;
      if (row) {
        // Prime React Query cache; useQuery will read this instead of refetching.
        try {
          context.queryClient.setQueryData(["auction", id], row);
        } catch { /* ignore */ }
        const canonical = buildAuctionSlug(row as never);
        if (canonical !== params.slug) {
          throw redirect({ to: "/auction/$slug", params: { slug: canonical }, replace: true });
        }
      }
      // Return only the SEO-relevant subset for head()/meta serialization.
      return { auction: (row as AuctionSeo) ?? null };
    } catch (e) {
      if (e && typeof e === "object" && "isRedirect" in e) throw e;
      return { auction: null };
    }
  },
  head: ({ params, loaderData }) => {
    const url = `https://bidnovaauctions.uk/auction/${params.slug}`;
    const a = loaderData?.auction;
    if (!a) {
      return {
        meta: [
          { title: "Auction — BidNova Auctions" },
          { name: "description", content: "Graded trading card auction on BidNova Auctions." },
          { property: "og:url", content: url },
          { property: "og:type", content: "website" },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const product = (a.product_type ?? "trading_card").toLowerCase();
    const isAccessory = product === "accessory";
    const isSealed = product === "sealed_product";
    const grade = isAccessory || isSealed ? "" : [a.grading_company, a.grade].filter(Boolean).join(" ");
    const title = `${a.title}${grade ? ` (${grade})` : ""} — BidNova Auctions`;
    const desc =
      (a.description?.trim() ||
        (isAccessory
          ? `Buy ${a.title} on BidNova Auctions — UK trading card accessory auction in GBP.`
          : isSealed
          ? `Bid on sealed ${a.title} on BidNova Auctions in GBP.`
          : `Bid on ${a.title}${grade ? ` graded ${grade}` : ""} in GBP on BidNova Auctions.`))
        .replace(/\s+/g, " ")
        .slice(0, 155);
    const image = a.images?.[0];
    const price = ((a.current_bid_pence ?? a.starting_price_pence ?? 0) / 100).toFixed(2);
    const availability =
      a.status === "active" ? "https://schema.org/InStock" : "https://schema.org/SoldOut";
    const category = isAccessory
      ? "Trading Card Accessories"
      : isSealed
      ? "Sealed Trading Card Products"
      : "Trading Cards";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "product" },
        ...(image
          ? [
              { property: "og:image", content: image } as const,
              { name: "twitter:image", content: image } as const,
            ]
          : []),
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(image ? [{ property: "og:image:alt", content: buildAuctionAlt(a) } as const] : []),
      ],
      links: [
        { rel: "canonical", href: url },
        ...(image
          ? [{
              rel: "preload",
              as: "image",
              href: transformSupabaseImage(image, { width: 720, quality: 72 }),
              imagesrcset: [360, 480, 720, 1080, 1440]
                .map((w) => `${transformSupabaseImage(image, { width: w, quality: 72 })} ${w}w`)
                .join(", "),
              imagesizes: "(max-width: 640px) 100vw, (max-width: 1024px) 60vw, 640px",
              fetchpriority: "high",
            } as const]
          : []),

      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildProductLd({
              url,
              name: a.title,
              description: desc,
              image,
              category,
              brand: a.brand,
              sku: a.id,
              validFrom: a.created_at ?? a.start_time ?? undefined,
              priceValidUntil: a.end_time ?? undefined,
              price,
              availability,
              itemCondition:
                a.condition === "factory_sealed" || a.condition === "brand_new"
                  ? "https://schema.org/NewCondition"
                  : "https://schema.org/UsedCondition",
              extra: {
                ...(a.product_line_set ? { isRelatedTo: a.product_line_set } : {}),
                ...(a.compatible_game ? { isAccessoryOrSparePartFor: a.compatible_game } : {}),
              },
            }),
          ),
        },


        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://bidnovaauctions.uk/" },
              { "@type": "ListItem", position: 2, name: "Auctions", item: "https://bidnovaauctions.uk/auctions" },
              { "@type": "ListItem", position: 3, name: a.title, item: url },
            ],
          }),
        },

        auctionFaqLd(buildAuctionFaq(a)),
      ],
    };
  },
  component: SlugRouteComponent,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function SlugRouteComponent() {
  const { slug } = Route.useParams();
  const id = extractIdFromSlug(slug);
  const { auction } = Route.useLoaderData();
  if (!id) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="font-display text-3xl text-gold-gradient">Auction not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">This URL doesn't look right — try browsing live auctions.</p>
      </div>
    );
  }
  return (
    <AuctionDetailInner
      id={id}
      initialAuction={auction && (auction as { id?: string }).id === id ? (auction as Record<string, unknown>) : null}
    />
  );
}
